import { useNavigate } from 'react-router-dom';
import { MODULE_CONFIG } from '../../config/subscriptionConfig';
import { useSubscriptionContext } from '../../contexts/SubscriptionContext';
import { useUserContext } from '../../contexts/UserContext';
import {
    calculateProration,
    formatCurrency,
    formatDate,
} from '../../utils/subscriptionUtils';
import { CustomModal } from '../CustomModal';
import { Btn, IconCheckCircle, IconShield } from '../ui';

const UNLOCK_FEATURES = [
    {
        title: 'Plan Builder.',
        body: 'Build MSHA/OSHA training plans with categories, time targets, and cycle duration.',
    },
    {
        title: 'Schedule Builder.',
        body: 'Auto-schedule topics across 365 days. Working days, weekends, holidays.',
    },
    {
        title: 'User compliance tracking.',
        body: 'Per-user progress, behind-schedule alerts, hire-date-relative enrollment.',
    },
    {
        title: 'Permanent training records & affidavits.',
        body: 'Audit-ready logs and one-click compliance reports.',
    },
] as const;

interface UpgradeTrainingModalProps {
    visible: boolean;
    topicCount: number;
    onDismiss: () => void;
}

export function UpgradeTrainingModal({
    visible,
    topicCount,
    onDismiss,
}: UpgradeTrainingModalProps) {
    const navigate = useNavigate();
    const { company } = useUserContext();
    const { monthlySummary } = useSubscriptionContext();
    const training = MODULE_CONFIG.training;

    const companyData = company as Record<string, unknown> | null;
    const billingAnchorDay = (companyData?.billingAnchorDay as number) || 15;
    const proration = calculateProration(training.monthlyRate, billingAnchorDay);
    const newMonthlyTotal = monthlySummary.total + training.monthlyRate;
    const topicPhrase = `${topicCount} topic${topicCount === 1 ? '' : 's'}`;

    const handleUpgrade = () => {
        onDismiss();
        navigate('/manage/subscription');
    };

    return (
        <CustomModal
            visible={visible}
            onDismiss={onDismiss}
            header="Upgrade to Safety & Training"
            size="default"
            contentClassName="db-upgrade-modal-panel"
            bodyClassName="db-upgrade-modal-body"
            describedById="db-upgrade-modal-desc"
        >
            <div className="db-upgrade">
                <div className="db-upgrade-hero">
                    <div className="db-upgrade-hero-icon btn-icon-wrap" aria-hidden="true">
                        <IconShield iconClasses="db-upgrade-hero-shield" />
                    </div>
                    <p className="db-upgrade-hero-title" aria-hidden="true">
                        Upgrade to Safety &amp; Training
                    </p>
                    <p id="db-upgrade-modal-desc" className="db-upgrade-hero-sub">
                        Keep your {topicPhrase}, gain full training plans, compliance tracking, and
                        audit-ready records.
                    </p>
                </div>

                <div className="db-upgrade-body">
                    <div className="db-upgrade-move" role="note">
                        <IconCheckCircle iconClasses="db-upgrade-move-icon" />
                        <div className="db-upgrade-move-copy">
                            <p className="db-upgrade-move-title">
                                Your Daily Briefing topics move with you
                            </p>
                            <p className="db-upgrade-move-text">
                                All {topicPhrase} in your current library automatically appear in
                                your new Safety &amp; Training Master Topic List. Nothing is lost.
                                You can then assign them to plans, categories, and schedules.
                            </p>
                        </div>
                    </div>

                    <div className="db-upgrade-unlock">
                        <p className="db-upgrade-unlock-label">What you&apos;ll unlock</p>
                        <ul className="db-upgrade-unlock-list">
                            {UNLOCK_FEATURES.map((feature) => (
                                <li key={feature.title}>
                                    <IconCheckCircle iconClasses="db-upgrade-check" />
                                    <span>
                                        <strong>{feature.title}</strong> {feature.body}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="db-upgrade-price">
                        <div className="db-upgrade-price-copy">
                            <p className="db-upgrade-price-label">Safety &amp; Training add-on</p>
                            <p className="db-upgrade-price-meta">Per company · unlimited users</p>
                        </div>
                        <p className="db-upgrade-price-amount">
                            ${Number.isInteger(training.monthlyRate)
                                ? training.monthlyRate.toFixed(0)
                                : training.monthlyRate.toFixed(2)}
                            /mo
                        </p>
                    </div>

                    <p className="db-upgrade-proration">
                        Prorated {formatCurrency(proration.proratedAmount)} today (
                        {proration.daysRemaining} days remaining in cycle). Your new monthly total
                        will be {formatCurrency(newMonthlyTotal)} starting{' '}
                        {formatDate(proration.nextFullChargeDate)}.
                    </p>

                    <div className="db-upgrade-actions">
                        <Btn
                            variant="warning"
                            buttonClasses="db-upgrade-cta"
                            buttonText={`Add Safety & Training — ${formatCurrency(proration.proratedAmount)} prorated today`}
                            buttonOnClick={handleUpgrade}
                        />
                        <Btn
                            variant="link"
                            buttonClasses="db-upgrade-dismiss"
                            buttonText="Not now, keep using Daily Briefing"
                            buttonOnClick={onDismiss}
                        />
                    </div>
                </div>
            </div>
        </CustomModal>
    );
}
